'use client'

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

import { useTranslation } from '@/hooks/client'
import { cx } from '@/lib/utils'

interface HeroCarouselProps {
  children: ReactNode[]
}

export const HeroCarousel = ({ children }: HeroCarouselProps) => {
  const { t } = useTranslation()
  const scrollRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [opacity, setOpacity] = useState(1)

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY
      const maxScroll = window.innerHeight
      const newOpacity = 1 - (scrollY * 0.8) / maxScroll
      setOpacity(Math.max(0.2, Math.min(1, newOpacity)))
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleSnap = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const scrollLeft = el.scrollLeft
    const itemWidth = el.firstElementChild?.clientWidth ?? 1
    setActiveIndex(Math.round(scrollLeft / itemWidth))
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    el.addEventListener('scroll', handleSnap, { passive: true })
    return () => el.removeEventListener('scroll', handleSnap)
  }, [handleSnap])

  return (
    <div style={{ opacity }}>
      <div
        ref={scrollRef}
        className={cx(
          'flex snap-x snap-mandatory gap-0 overflow-x-auto [scrollbar-width:none]',
          'lg:absolute lg:bottom-[80px] lg:ml-[calc(100%-456px)] lg:w-[360px] lg:overflow-hidden',
          'desktop-xl:bottom-[126px] desktop-xl:w-[452px] desktop-xl:ml-[calc(100%-644px)]'
        )}
      >
        {children.map((child, i) => (
          <div key={i} className="w-full shrink-0 snap-start">
            {child}
          </div>
        ))}
      </div>
      {children.length > 1 && (
        <div
          className={cx(
            'flex gap-[10px]',
            'max-lg:mx-auto max-lg:w-full max-lg:max-w-[399px] max-lg:gap-[8px] max-lg:p-[2px_0px_6px_0px]',
            'lg:absolute lg:right-12 lg:bottom-0 lg:w-[360px] lg:p-[10px_0px_26px_0px]',
            'desktop-xl:right-24 desktop-xl:w-[452px] desktop-xl:gap-[12px] desktop-xl:p-[18px_0px_64px_0px]'
          )}
          style={{ opacity }}
        >
          {children.map((_, i) => (
            <button
              key={i}
              type="button"
              data-tracking="ignore"
              aria-label={t('client.carousel.goToSlide', 'Go to slide {{n}}', { n: i + 1 })}
              onClick={() => {
                scrollRef.current?.children[i]?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'nearest',
                  inline: 'start',
                })
              }}
              className="flex min-h-11 flex-1 cursor-pointer items-center border-none bg-transparent p-0 outline-hidden focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky"
            >
              <div
                className={cx(
                  'bg-sky desktop-xl:h-[8px] h-[4px] w-full rounded-[3px] lg:h-[6px]',
                  i === activeIndex ? 'opacity-100' : 'opacity-10 hover:opacity-100'
                )}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
